import { db } from "../firebaseConfig";
import {
  getDoc,
  getDocs,
  setDoc,
  collection,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { auth } from "../firebaseConfig";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  AuthErrorCodes,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { TableName } from "../constants/tableName";
import { RoleType } from "../constants/roleType";

export class BOUsersController {
  static TABLE_NAME = TableName.BOUSERS;

  static signUp = ({ parameters, resolve, reject }) => {
    let errorMessage =
      "Unable to sign up.\nPlease contact administrator for assistance.";
    createUserWithEmailAndPassword(auth, parameters.email, parameters.password)
      .then((userCredential) => {
        getDoc(doc(db, this.TABLE_NAME, userCredential.user.uid))
          .then((data) => {
            if (!data.exists()) {
              setDoc(doc(db, this.TABLE_NAME, userCredential.user.uid), {
                firstName: parameters.firstName,
                lastName: parameters.lastName,
                email: parameters.email,
              })
                .then(() => {
                  sendEmailVerification(userCredential.user)
                    .then(() => {
                      resolve(
                        "Please proceed to your email account for varification."
                      );
                    })
                    .catch((error) => {
                      reject(
                        this.errorHandling({
                          errorMessage: "Failed to send verification email:",
                          error: error,
                        })
                      );
                    });
                })
                .catch((error) => {
                  reject(
                    this.errorHandling({
                      errorMessage: errorMessage,
                      error: error,
                    })
                  );
                });
            } else {
              reject("This email has been registered.");
            }
          })
          .catch((error) => {
            reject(
              this.errorHandling({ errorMessage: errorMessage, error: error })
            );
          });
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static login = ({ parameters, resolve, reject }) => {
    let errorMessage =
      "Unable to login.\nPlease contact administrator for assistance.";
    signInWithEmailAndPassword(auth, parameters.email, parameters.password)
      .then((userCredential) => {
        if (!userCredential.user.emailVerified) {
          reject(
            this.errorHandling({
              errorMessage:
                "Email not verified. Please check your inbox for the verification email.",
              error: null,
            })
          );
        } else {
          getDoc(doc(db, this.TABLE_NAME, userCredential.user.uid)).then(
            (data) => {
              if (!data.data())
                reject(
                  this.errorHandling({
                    errorMessage: "User not found.",
                    error: null,
                  })
                );
              if (data.data()?.role !== RoleType.ADMIN)
                reject(
                  this.errorHandling({
                    errorMessage: "This user is not admin.",
                    error: null,
                  })
                );
              localStorage.setItem("user", JSON.stringify(userCredential.user));
              console.debug(userCredential.user);
              resolve(userCredential.user);
            }
          );
        }
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static forgotPassword = ({ email, resolve, reject }) => {
    let errorMessage =
      "Unable to send password reset email.\nPlease contact the administrator for assistance.";
    sendPasswordResetEmail(auth, email)
      .then(() => {
        resolve("Password reset email has been sent. Please check your inbox.");
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static logout = ({ resolve, reject }) => {
    let errorMessage =
      "Unable to logout.\nPlease contact administrator for assistance.";
    signOut(auth)
      .then(() => {
        localStorage.removeItem("user");
        localStorage.removeItem("colors");
        resolve("Logout successfully.");
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static getAll = ({ resolve, reject }) => {
    let errorMessage =
      "Unable to get all the BO users details.\nPlease contact administrator for assistance.";
    getDocs(collection(db, this.TABLE_NAME))
      .then((data) => {
        console.debug(data);
        resolve(data);
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static add = ({ parameters, resolve, reject }) => {
    let errorMessage =
      "Unable to create new BO users details.\nPlease contact administrator for assistance.";
    createUserWithEmailAndPassword(auth, parameters.email, parameters.password)
      .then((userCredential) => {
        getDoc(doc(db, this.TABLE_NAME, userCredential.user.uid))
          .then((data) => {
            if (!data.exists()) {
              setDoc(
                doc(db, this.TABLE_NAME, userCredential.user.uid),
                parameters
              )
                .then(() => {
                  sendEmailVerification(userCredential.user)
                    .then(() => {
                      resolve(
                        "New BO users details added to firebase successfully.\nPlease ask user to varified their account via email."
                      );
                    })
                    .catch((error) => {
                      reject(
                        this.errorHandling({
                          errorMessage: "Failed to send verification email:",
                          error: error,
                        })
                      );
                    });
                })
                .catch((error) => {
                  reject(
                    this.errorHandling({
                      errorMessage: errorMessage,
                      error: error,
                    })
                  );
                });
            } else {
              reject("This email has been registered.");
            }
          })
          .catch((error) => {
            reject(
              this.errorHandling({ errorMessage: errorMessage, error: error })
            );
          });
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static update = ({ parameters, resolve, reject }) => {
    let errorMessage =
      "Unable to update the selected BO users details.\nPlease contact administrator for assistance.";
    updateDoc(doc(db, this.TABLE_NAME, parameters.id), parameters)
      .then(() => {
        resolve("Users details updated to firebase successfully.");
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static deleteAll = ({ idList, resolve, reject }) => {
    let errorMessage =
      "Unable to delete all the BO users details.\nPlease contact administrator for assistance.";
    const deletePromises = idList.map((id) => {
      return deleteDoc(doc(db, this.TABLE_NAME, id)).catch((error) => {
        console.debug(error.message);
        throw error;
      });
    });
    Promise.all(deletePromises)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(
          this.errorHandling({ errorMessage: errorMessage, error: error })
        );
      });
  };

  static errorHandling = ({ errorMessage, error }) => {
    if (error === null) return errorMessage;
    switch (error.code) {
      case AuthErrorCodes.EMAIL_EXISTS:
        errorMessage = "This email address is already in use.";
        break;
      case AuthErrorCodes.INVALID_EMAIL:
        errorMessage =
          "Email address provided is invalid.\nPlease use the correct email format or use a different email address.";
        break;
      case AuthErrorCodes.WEAK_PASSWORD:
        errorMessage = "Please provide a stronger password.";
        break;
      case AuthErrorCodes.USER_DISABLED:
        errorMessage =
          "This account has been disabled.\nPlease contact administrator for assistance.";
        break;
      case AuthErrorCodes.USER_DELETED:
      case AuthErrorCodes.INVALID_PASSWORD:
      case AuthErrorCodes.INVALID_LOGIN_CREDENTIALS:
        errorMessage = "Incorrect email or password.";
        break;
      case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
        errorMessage = "Too many attempts.\nPlease try again in a few minutes.";
        break;
      case "auth/missing-email":
        errorMessage = "Missing email address.";
        break;
      default:
        break;
    }
    console.debug(error.message);
    return errorMessage;
  };
}
